import i18next, { type i18n } from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * Arabic and English with RTL/LTR support (spec sections 86 and 87). Both apps share these resources
 * so a term is translated once.
 */
export const SUPPORTED_LANGUAGES = ['en', 'ar'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export function isRtl(language: string): boolean {
  return language.startsWith('ar');
}

/** Applies direction and language to the document so CSS logical properties do the rest. */
export function applyDocumentDirection(language: string): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.lang = language;
  document.documentElement.dir = isRtl(language) ? 'rtl' : 'ltr';
}

const en = {
  common: {
    appName: 'Barah Pay',
    search: 'Search',
    filter: 'Filter',
    reset: 'Reset',
    failure: 'Failure',
    apply: 'Apply',
    refresh: 'Refresh',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    actions: 'Actions',
    details: 'Details',
    export: 'Export',
    loading: 'Loading',
    noData: 'Nothing to show',
    error: 'Something went wrong',
    retry: 'Try again',
    yes: 'Yes',
    no: 'No',
    all: 'All',
    from: 'From',
    to: 'To',
    reason: 'Reason',
    notes: 'Notes',
    amount: 'Amount',
    currency: 'Currency',
    status: 'Status',
    createdAt: 'Created',
    updatedAt: 'Updated',
    signOut: 'Sign out',
    language: 'Language',
    correlationId: 'Correlation ID',
    copied: 'Copied',
    required: 'This field is required',
    page: 'Page',
    of: 'of',
  },
  navGroups: {
    overview: 'Overview',
    money: 'Money movement',
    customers: 'Customers and billing',
    catalog: 'Catalogue',
    platform: 'Platform',
    administration: 'Administration',
  },
  nav: {
    dashboard: 'Dashboard',
    payments: 'Payments',
    paymentLinks: 'Payment links',
    customers: 'Customers',
    subscriptions: 'Subscriptions',
    plans: 'Plans',
    billing: 'Billing',
    refunds: 'Refunds',
    disputes: 'Disputes',
    settlements: 'Settlements',
    reconciliation: 'Reconciliation',
    products: 'Products',
    providers: 'Providers',
    webhooks: 'Webhooks',
    events: 'Events',
    reports: 'Reports',
    users: 'Users',
    roles: 'Roles and permissions',
    auditLogs: 'Audit logs',
    systemHealth: 'System health',
    settings: 'Settings',
    investigations: 'Investigations',
  },
  auth: {
    signIn: 'Sign in',
    signInSubtitle: 'Barah Pay administration',
    email: 'Email address',
    password: 'Password',
    mfaCode: 'Verification code',
    mfaRequired: 'Enter the code from your authenticator app',
    signInFailed: 'Sign-in failed',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    mustChangePassword: 'Your password must be changed before you continue.',
    sessionExpired: 'Your session has expired. Please sign in again.',
    welcome: 'Welcome back',
    headline: 'One payment platform for every Barah product',
    lede: 'Payments, subscriptions, refunds, settlement and reconciliation for Meezan 360, BarahBot, CRM and Rikaz — behind a single audited control panel.',
    featurePayments: 'Payments and refunds',
    featureSubscriptions: 'Subscriptions and billing',
    featureReconciliation: 'Settlement reconciliation',
    featureAudit: 'Audited access',
    statScopeValue: 'SAQ A',
    statScopeLabel: 'PCI DSS scope — no card data is stored here',
    statLanguagesValue: 'AR · EN',
    statLanguagesLabel: 'Bilingual portal with full RTL support',
    statMonitoringValue: '24/7',
    statMonitoringLabel: 'Automated provider health monitoring',
  },
  dashboard: {
    todayVolume: "Today's volume",
    todaySuccessful: "Today's successful payments",
    todayFailed: "Today's failed payments",
    todayRefunds: "Today's refunds",
    pendingPayments: 'Pending payments',
    activeSubscriptions: 'Active subscriptions',
    pastDueSubscriptions: 'Past due subscriptions',
    providerHealth: 'Provider health',
    unreconciled: 'Unreconciled transactions',
    nextSettlement: 'Next settlement',
    successRate: 'Success rate',
    openDisputes: 'Open disputes',
    underInvestigation: 'Under investigation',
    openAlerts: 'Open alerts',
    deadLetters: 'Dead-lettered deliveries',
    volumeByDay: 'Payment volume by day',
    successRateByDay: 'Payment success rate',
    paymentsByProduct: 'Payments by product',
    paymentsByMethod: 'Payments by method',
    subscriptionsByProduct: 'Subscriptions by product',
    failureReasons: 'Failed payment reasons',
    settlementTrends: 'Settlement trends',
  },
  payments: {
    title: 'Payments',
    paymentNumber: 'Payment',
    reference: 'Reference',
    product: 'Product',
    tenant: 'Tenant',
    customer: 'Customer',
    method: 'Method',
    channel: 'Channel',
    provider: 'Provider',
    providerTransaction: 'Provider transaction',
    refunded: 'Refunded',
    refundable: 'Refundable',
    timeline: 'Timeline',
    attempts: 'Attempts',
    refunds: 'Refunds',
    webhookEvents: 'Webhook events',
    internalEvents: 'Internal events',
    disputes: 'Disputes',
    settlement: 'Settlement',
    auditHistory: 'Audit history',
    investigation: 'Investigation',
    manualDetail: 'Manual payment detail',
    metadata: 'Metadata',
    cancel: 'Cancel payment',
    syncProvider: 'Sync with provider',
    void: 'Void authorisation',
    requestRefund: 'Request refund',
    openInvestigation: 'Open investigation',
    resolveInvestigation: 'Resolve investigation',
    checkoutUrl: 'Hosted checkout URL',
    amountRange: 'Amount range',
    dateRange: 'Date range',
    underInvestigation: 'Under investigation',
    hasOpenDispute: 'Has an open dispute',
  },
  refunds: {
    title: 'Refunds',
    approve: 'Approve',
    reject: 'Reject',
    process: 'Send to provider',
    approvalQueue: 'Approval queue',
    awaitingApproval: 'Awaiting approval',
    requestedBy: 'Requested by',
    approvedBy: 'Approved by',
    refundType: 'Type',
    approvalRequired: 'Approval required',
    partialAmount: 'Partial amount (leave blank for the full remaining balance)',
    cannotApproveOwn: 'You requested this refund, so a different approver must decide it.',
  },
  subscriptions: {
    title: 'Subscriptions',
    plan: 'Plan',
    currentPeriod: 'Current period',
    nextBilling: 'Next billing',
    gracePeriodEnd: 'Grace period ends',
    grantsAccess: 'Access granted',
    paymentMethod: 'Payment method',
    renewalFailures: 'Renewal failures',
    cycles: 'Billing cycles',
    cancel: 'Cancel',
    cancelAtPeriodEnd: 'Cancel at period end',
    resume: 'Resume',
    suspend: 'Suspend',
    activate: 'Activate',
    changePlan: 'Change plan',
    retryPayment: 'Retry renewal',
    extendTrial: 'Extend trial',
    strategy: 'Renewal strategy',
    fallbackIssued: 'A payment link was issued because the stored card could not be charged.',
  },
  providers: {
    title: 'Providers',
    capabilities: 'Capabilities',
    configuration: 'Configuration',
    merchantAccounts: 'Merchant accounts',
    environment: 'Environment',
    health: 'Health',
    circuitOpen: 'Circuit open',
    closeCircuit: 'Close circuit',
    unconfirmed: 'Unconfirmed with the bank',
    supportsRecurring: 'Recurring',
    supportsTokenization: 'Tokenisation',
    supportsRefund: 'Refund',
    supportsPartialRefund: 'Partial refund',
    supportsVoid: 'Void',
    supports3ds: '3-D Secure',
    credentialsNotShown: 'Credentials are never displayed. Enter a value only to replace it.',
    justification: 'Justification (required for production credential changes)',
  },
  settlements: {
    title: 'Settlements',
    gross: 'Gross',
    fees: 'Provider fees',
    vatOnFees: 'VAT on fees',
    net: 'Net settlement',
    variance: 'Variance',
    bankReference: 'Bank reference',
    import: 'Import file',
    fetch: 'Fetch from provider',
    close: 'Close settlement',
    matched: 'Matched',
    unmatched: 'Unmatched',
  },
  reconciliation: {
    title: 'Reconciliation',
    run: 'Run reconciliation',
    outcome: 'Outcome',
    barahPayAmount: 'Barah Pay',
    providerAmount: 'Provider',
    resolve: 'Resolve',
    accept: 'Accept difference',
    escalate: 'Open investigation',
    items: 'Discrepancies',
    matched: 'Matched',
  },
  webhooks: {
    title: 'Webhooks',
    inbound: 'Inbound from providers',
    outbound: 'Outbound to products',
    signatureValid: 'Signature valid',
    duplicate: 'Duplicate',
    replay: 'Replay',
    retry: 'Retry',
    resolve: 'Mark resolved',
    deadLetter: 'Dead letter',
    payload: 'Payload',
    attempts: 'Attempts',
    targetUrl: 'Target URL',
  },
  reports: {
    title: 'Reports',
    run: 'Run report',
    exportCsv: 'Export CSV',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    totals: 'Totals',
    generatedAt: 'Generated',
  },
  settings: {
    title: 'Settings',
    featureFlags: 'Feature flags',
    systemSettings: 'System settings',
    category: 'Category',
    value: 'Value',
    sensitive: 'Sensitive',
    updatedBy: 'Updated by',
  },
  users: {
    title: 'Users',
    fullName: 'Full name',
    roles: 'Roles',
    mfa: 'MFA',
    lockedOut: 'Locked out',
    lastLogin: 'Last sign-in',
    createUser: 'Add user',
    resetPassword: 'Reset password',
    apiClients: 'API clients',
    scopes: 'Scopes',
    rotateSecret: 'Rotate secret',
    secretShownOnce: 'Copy this secret now. It cannot be shown again.',
    permissions: 'Permissions',
    createRole: 'Add role',
  },
  paymentLinks: {
    title: 'Payment links',
    create: 'Create link',
    linkId: 'Link',
    url: 'URL',
    amountMode: 'Amount mode',
    fixed: 'Fixed amount',
    customerEntered: 'Customer enters the amount',
    minAmount: 'Minimum amount',
    maxAmount: 'Maximum amount',
    singleUse: 'Single use',
    paymentCount: 'Payments',
    expiresAt: 'Expires',
    expire: 'Expire now',
    copyUrl: 'Copy URL',
    referenceType: 'Reference type',
    referenceId: 'Reference',
  },
  customers: {
    title: 'Customers',
    customerNumber: 'Customer',
    name: 'Name',
    email: 'Email',
    mobile: 'Mobile',
    externalId: 'Product customer id',
    paymentMethods: 'Saved payment methods',
    anonymised: 'Anonymised',
    anonymise: 'Anonymise (PDPL erasure)',
    anonymiseWarning:
      'Personal data is overwritten and cannot be recovered. Financial records are preserved.',
    defaultMethod: 'Default',
    usable: 'Usable',
    expired: 'Expired',
    payments: 'Payments',
    subscriptions: 'Subscriptions',
  },
  plans: {
    title: 'Plans',
    code: 'Code',
    name: 'Name',
    interval: 'Billing interval',
    price: 'Price',
    monthlyEquivalent: 'Monthly equivalent',
    trialDays: 'Trial days',
    graceDays: 'Grace days',
    vatRate: 'VAT rate',
    customPriced: 'Custom priced',
    active: 'Active',
    features: 'Features',
    create: 'Add plan',
    edit: 'Edit plan',
    sortOrder: 'Sort order',
  },
  products: {
    title: 'Products',
    code: 'Code',
    name: 'Name',
    webhookUrl: 'Webhook URL',
    webhookSecret: 'Webhook secret',
    generateSecret: 'Generate secret',
    rotateSecret: 'Rotate secret',
    redirectUrls: 'Allowed redirect URLs',
    addRedirectUrl: 'Add redirect URL',
    allowSubPaths: 'Allow sub-paths',
    kind: 'Kind',
    tenants: 'Tenants',
    defaultCurrency: 'Default currency',
    expiryMinutes: 'Payment expiry (minutes)',
    vatRate: 'VAT rate',
    prorateUpgrades: 'Charge upgrades immediately',
    statementDescriptor: 'Statement descriptor',
    supportEmail: 'Support email',
    create: 'Add product',
    edit: 'Edit product',
    active: 'Active',
    secretShownOnce: 'Copy this secret now. It is stored hashed and cannot be shown again.',
  },
  billing: {
    title: 'Billing',
    invoiceNumber: 'Invoice',
    subtotal: 'Subtotal',
    discount: 'Discount',
    vat: 'VAT',
    total: 'Total',
    amountPaid: 'Paid',
    amountDue: 'Due',
    dueDate: 'Due date',
    issuedAt: 'Issued',
    paidAt: 'Paid at',
    period: 'Period',
    unpaidOnly: 'Unpaid only',
    lineItems: 'Line items',
    quantity: 'Quantity',
    unitPrice: 'Unit price',
    lineTotal: 'Line total',
    notAccounting: 'Barah Pay invoices are internal records. Meezan remains the accounting source of truth.',
  },
  events: {
    title: 'Events',
    eventNumber: 'Event',
    eventType: 'Type',
    version: 'Version',
    aggregate: 'Aggregate',
    occurredAt: 'Occurred',
    publishedAt: 'Published',
    nextAttempt: 'Next attempt',
    lastError: 'Last error',
    catalogue: 'Event catalogue',
    payload: 'Payload',
    retry: 'Retry now',
  },
  audit: {
    title: 'Audit logs',
    action: 'Action',
    entityType: 'Entity type',
    entityId: 'Entity',
    actor: 'Actor',
    actorType: 'Actor type',
    ipAddress: 'IP address',
    oldValue: 'Before',
    newValue: 'After',
    succeeded: 'Succeeded',
    occurredAt: 'When',
    appendOnly: 'Audit records are append-only and are never edited or deleted.',
  },
  health: {
    title: 'System health',
    environment: 'Environment',
    version: 'Version',
    database: 'Database',
    reachable: 'Reachable',
    unreachable: 'Unreachable',
    eventTransport: 'Event transport',
    cacheProvider: 'Cache provider',
    queues: 'Queues and backlogs',
    outboxPending: 'Outbox pending',
    outboxFailed: 'Outbox failed',
    outboxDeadLetter: 'Outbox dead-lettered',
    productWebhooksPending: 'Product webhooks pending',
    productWebhooksDeadLetter: 'Product webhooks dead-lettered',
    notificationsQueued: 'Notifications queued',
    webhooksAwaiting: 'Inbound webhooks awaiting processing',
    openAlerts: 'Open alerts',
    adapters: 'Registered provider adapters',
    serverTime: 'Server time',
    alerts: 'Alerts',
    notifications: 'Notifications',
    acknowledge: 'Acknowledge',
    resolveAlert: 'Resolve',
    severity: 'Severity',
    raisedAt: 'Raised',
    occurrences: 'Occurrences',
  },
  investigations: {
    title: 'Investigations',
    reason: 'Reason',
    resolution: 'Resolution',
    openedBy: 'Opened by',
    openedAt: 'Opened',
    resolvedBy: 'Resolved by',
    statusAtOpen: 'Status when opened',
    providerReported: 'Provider reported',
    lateSuccessNotice:
      'A provider reported success after the payment had expired. Money is never written off silently — resolve the investigation to record the outcome.',
    resolve: 'Resolve',
    confirmProviderSuccess: 'Confirm the provider succeeded',
    confirmProviderFailure: 'Confirm the provider did not succeed',
    refundIssued: 'Refunded to the customer',
    noActionRequired: 'No action required',
  },
  checkout: {
    payNow: 'Pay now',
    amountToPay: 'Amount to pay',
    enterAmount: 'Enter the amount to pay',
    choosePaymentMethod: 'Choose a payment method',
    securePayment: 'Secure payment',
    securedBy: 'Payments are processed securely by Barah Pay.',
    poweredBy: 'Powered by Barah Pay',
    expiresAt: 'This payment request expires',
    reference: 'Reference',
    paymentSuccessful: 'Payment successful',
    paymentFailed: 'Payment not completed',
    paymentPending: 'Waiting for payment',
    paymentExpired: 'This payment request has expired',
    paymentCancelled: 'Payment cancelled',
    underReview: 'Your payment is being reviewed',
    returnToMerchant: 'Return to {{merchant}}',
    redirecting: 'Taking you to your bank securely',
    notAvailable: 'This payment page is no longer available',
    tryAgain: 'Try again',
    contactSupport: 'Contact support',
    minAmount: 'Minimum {{amount}}',
    maxAmount: 'Maximum {{amount}}',
    cardDetailsNotice: 'Card details are entered on your bank’s page, never on Barah Pay.',
    threeDSecure: '3-D Secure',
  },
};

const ar: typeof en = {
  common: {
    appName: 'براح باي',
    search: 'بحث',
    filter: 'تصفية',
    reset: 'إعادة تعيين',
    failure: 'الخطأ',
    apply: 'تطبيق',
    refresh: 'تحديث',
    save: 'حفظ',
    cancel: 'إلغاء',
    close: 'إغلاق',
    confirm: 'تأكيد',
    actions: 'الإجراءات',
    details: 'التفاصيل',
    export: 'تصدير',
    loading: 'جارٍ التحميل',
    noData: 'لا توجد بيانات',
    error: 'حدث خطأ',
    retry: 'إعادة المحاولة',
    yes: 'نعم',
    no: 'لا',
    all: 'الكل',
    from: 'من',
    to: 'إلى',
    reason: 'السبب',
    notes: 'ملاحظات',
    amount: 'المبلغ',
    currency: 'العملة',
    status: 'الحالة',
    createdAt: 'تاريخ الإنشاء',
    updatedAt: 'آخر تحديث',
    signOut: 'تسجيل الخروج',
    language: 'اللغة',
    correlationId: 'معرف التتبع',
    copied: 'تم النسخ',
    required: 'هذا الحقل مطلوب',
    page: 'صفحة',
    of: 'من',
  },
  navGroups: {
    overview: 'نظرة عامة',
    money: 'حركة الأموال',
    customers: 'العملاء والفواتير',
    catalog: 'الكتالوج',
    platform: 'المنصة',
    administration: 'الإدارة',
  },
  nav: {
    dashboard: 'لوحة المعلومات',
    payments: 'المدفوعات',
    paymentLinks: 'روابط الدفع',
    customers: 'العملاء',
    subscriptions: 'الاشتراكات',
    plans: 'الخطط',
    billing: 'الفواتير',
    refunds: 'المبالغ المستردة',
    disputes: 'الاعتراضات',
    settlements: 'التسويات',
    reconciliation: 'المطابقة',
    products: 'المنتجات',
    providers: 'مزودو الدفع',
    webhooks: 'الويب هوك',
    events: 'الأحداث',
    reports: 'التقارير',
    users: 'المستخدمون',
    roles: 'الأدوار والصلاحيات',
    auditLogs: 'سجل التدقيق',
    systemHealth: 'حالة النظام',
    settings: 'الإعدادات',
    investigations: 'التحقيقات',
  },
  auth: {
    signIn: 'تسجيل الدخول',
    signInSubtitle: 'إدارة براح باي',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    mfaCode: 'رمز التحقق',
    mfaRequired: 'أدخل الرمز من تطبيق المصادقة',
    signInFailed: 'فشل تسجيل الدخول',
    changePassword: 'تغيير كلمة المرور',
    currentPassword: 'كلمة المرور الحالية',
    newPassword: 'كلمة المرور الجديدة',
    mustChangePassword: 'يجب تغيير كلمة المرور قبل المتابعة.',
    sessionExpired: 'انتهت الجلسة. الرجاء تسجيل الدخول مرة أخرى.',
    welcome: 'مرحباً بعودتك',
    headline: 'منصة مدفوعات واحدة لجميع منتجات براح',
    lede: 'المدفوعات والاشتراكات والمبالغ المستردة والتسويات والمطابقة لميزان 360 وبراح بوت وإدارة العلاقات وركاز — من لوحة تحكم واحدة موثّقة بالكامل.',
    featurePayments: 'المدفوعات والاسترداد',
    featureSubscriptions: 'الاشتراكات والفواتير',
    featureReconciliation: 'مطابقة التسويات',
    featureAudit: 'وصول موثّق بالسجلات',
    statScopeValue: 'SAQ A',
    statScopeLabel: 'نطاق PCI DSS — لا تُخزَّن بيانات البطاقات هنا',
    statLanguagesValue: 'ع · EN',
    statLanguagesLabel: 'لوحة ثنائية اللغة بدعم كامل للاتجاه من اليمين',
    statMonitoringValue: '24/7',
    statMonitoringLabel: 'مراقبة آلية لحالة مزودي الدفع',
  },
  dashboard: {
    todayVolume: 'إجمالي اليوم',
    todaySuccessful: 'المدفوعات الناجحة اليوم',
    todayFailed: 'المدفوعات الفاشلة اليوم',
    todayRefunds: 'المبالغ المستردة اليوم',
    pendingPayments: 'مدفوعات معلقة',
    activeSubscriptions: 'اشتراكات نشطة',
    pastDueSubscriptions: 'اشتراكات متأخرة',
    providerHealth: 'حالة مزودي الدفع',
    unreconciled: 'عمليات غير مطابقة',
    nextSettlement: 'التسوية القادمة',
    successRate: 'نسبة النجاح',
    openDisputes: 'اعتراضات مفتوحة',
    underInvestigation: 'تحت التحقيق',
    openAlerts: 'تنبيهات مفتوحة',
    deadLetters: 'إشعارات فاشلة نهائياً',
    volumeByDay: 'حجم المدفوعات باليوم',
    successRateByDay: 'نسبة نجاح المدفوعات',
    paymentsByProduct: 'المدفوعات بحسب المنتج',
    paymentsByMethod: 'المدفوعات بحسب الطريقة',
    subscriptionsByProduct: 'الاشتراكات بحسب المنتج',
    failureReasons: 'أسباب فشل المدفوعات',
    settlementTrends: 'اتجاهات التسوية',
  },
  payments: {
    title: 'المدفوعات',
    paymentNumber: 'رقم الدفع',
    reference: 'المرجع',
    product: 'المنتج',
    tenant: 'المستأجر',
    customer: 'العميل',
    method: 'طريقة الدفع',
    channel: 'القناة',
    provider: 'مزود الدفع',
    providerTransaction: 'معرف عملية المزود',
    refunded: 'المبلغ المسترد',
    refundable: 'القابل للاسترداد',
    timeline: 'التسلسل الزمني',
    attempts: 'المحاولات',
    refunds: 'المبالغ المستردة',
    webhookEvents: 'أحداث الويب هوك',
    internalEvents: 'الأحداث الداخلية',
    disputes: 'الاعتراضات',
    settlement: 'التسوية',
    auditHistory: 'سجل التدقيق',
    investigation: 'التحقيق',
    manualDetail: 'تفاصيل الدفع اليدوي',
    metadata: 'بيانات إضافية',
    cancel: 'إلغاء الدفع',
    syncProvider: 'مزامنة مع المزود',
    void: 'إلغاء التفويض',
    requestRefund: 'طلب استرداد',
    openInvestigation: 'بدء تحقيق',
    resolveInvestigation: 'إغلاق التحقيق',
    checkoutUrl: 'رابط صفحة الدفع',
    amountRange: 'نطاق المبلغ',
    dateRange: 'نطاق التاريخ',
    underInvestigation: 'تحت التحقيق',
    hasOpenDispute: 'يوجد اعتراض مفتوح',
  },
  refunds: {
    title: 'المبالغ المستردة',
    approve: 'اعتماد',
    reject: 'رفض',
    process: 'إرسال للمزود',
    approvalQueue: 'قائمة الاعتماد',
    awaitingApproval: 'بانتظار الاعتماد',
    requestedBy: 'مقدم الطلب',
    approvedBy: 'المعتمد',
    refundType: 'النوع',
    approvalRequired: 'يتطلب اعتماداً',
    partialAmount: 'مبلغ جزئي (اتركه فارغاً لاسترداد كامل المتبقي)',
    cannotApproveOwn: 'أنت مقدم طلب الاسترداد، لذا يجب أن يعتمده شخص آخر',
  },
  subscriptions: {
    title: 'الاشتراكات',
    plan: 'الخطة',
    currentPeriod: 'الفترة الحالية',
    nextBilling: 'الفاتورة القادمة',
    gracePeriodEnd: 'انتهاء فترة السماح',
    grantsAccess: 'الوصول مفعّل',
    paymentMethod: 'طريقة الدفع',
    renewalFailures: 'محاولات تجديد فاشلة',
    cycles: 'دورات الفوترة',
    cancel: 'إلغاء',
    cancelAtPeriodEnd: 'الإلغاء في نهاية الفترة',
    resume: 'استئناف',
    suspend: 'تعليق',
    activate: 'تنشيط',
    changePlan: 'تغيير الخطة',
    retryPayment: 'إعادة محاولة التجديد',
    extendTrial: 'تمديد الفترة التجريبية',
    strategy: 'طريقة التجديد',
    fallbackIssued: 'تم إصدار رابط دفع لعدم إمكانية خصم البطاقة المحفوظة.',
  },
  providers: {
    title: 'مزودو الدفع',
    capabilities: 'القدرات',
    configuration: 'الإعدادات',
    merchantAccounts: 'حسابات التاجر',
    environment: 'البيئة',
    health: 'الحالة',
    circuitOpen: 'الدائرة مفتوحة',
    closeCircuit: 'إغلاق الدائرة',
    unconfirmed: 'غير مؤكد من البنك',
    supportsRecurring: 'الدفع المتكرر',
    supportsTokenization: 'الترميز',
    supportsRefund: 'الاسترداد',
    supportsPartialRefund: 'استرداد جزئي',
    supportsVoid: 'إلغاء التفويض',
    supports3ds: 'التحقق الثلاثي',
    credentialsNotShown: 'لا تُعرض بيانات الاعتماد. أدخل قيمة فقط لاستبدالها.',
    justification: 'المبرر (مطلوب لتغيير بيانات الإنتاج)',
  },
  settlements: {
    title: 'التسويات',
    gross: 'الإجمالي',
    fees: 'عمولات المزود',
    vatOnFees: 'ضريبة العمولات',
    net: 'صافي التسوية',
    variance: 'الفرق',
    bankReference: 'مرجع البنك',
    import: 'استيراد ملف',
    fetch: 'جلب من المزود',
    close: 'إغلاق التسوية',
    matched: 'مطابق',
    unmatched: 'غير مطابق',
  },
  reconciliation: {
    title: 'المطابقة',
    run: 'تشغيل المطابقة',
    outcome: 'النتيجة',
    barahPayAmount: 'براح باي',
    providerAmount: 'المزود',
    resolve: 'معالجة',
    accept: 'قبول الفرق',
    escalate: 'بدء تحقيق',
    items: 'الفروقات',
    matched: 'مطابق',
  },
  webhooks: {
    title: 'الويب هوك',
    inbound: 'الوارد من المزودين',
    outbound: 'الصادر إلى المنتجات',
    signatureValid: 'التوقيع صحيح',
    duplicate: 'مكرر',
    replay: 'إعادة المعالجة',
    retry: 'إعادة المحاولة',
    resolve: 'تحديد كمعالج',
    deadLetter: 'فاشل نهائياً',
    payload: 'المحتوى',
    attempts: 'المحاولات',
    targetUrl: 'الرابط المستهدف',
  },
  reports: {
    title: 'التقارير',
    run: 'تشغيل التقرير',
    exportCsv: 'تصدير CSV',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    totals: 'الإجماليات',
    generatedAt: 'تاريخ الإصدار',
  },
  settings: {
    title: 'الإعدادات',
    featureFlags: 'مفاتيح الميزات',
    systemSettings: 'إعدادات النظام',
    category: 'التصنيف',
    value: 'القيمة',
    sensitive: 'حساس',
    updatedBy: 'آخر تعديل بواسطة',
  },
  users: {
    title: 'المستخدمون',
    fullName: 'الاسم الكامل',
    roles: 'الأدوار',
    mfa: 'المصادقة الثنائية',
    lockedOut: 'محظور مؤقتاً',
    lastLogin: 'آخر دخول',
    createUser: 'إضافة مستخدم',
    resetPassword: 'إعادة تعيين كلمة المرور',
    apiClients: 'عملاء الـ API',
    scopes: 'النطاقات',
    rotateSecret: 'تدوير السر',
    secretShownOnce: 'انسخ هذا السر الآن، لن يُعرض مرة أخرى.',
    permissions: 'الصلاحيات',
    createRole: 'إضافة دور',
  },
  paymentLinks: {
    title: 'روابط الدفع',
    create: 'إنشاء رابط',
    linkId: 'الرابط',
    url: 'العنوان',
    amountMode: 'نوع المبلغ',
    fixed: 'مبلغ ثابت',
    customerEntered: 'العميل يحدد المبلغ',
    minAmount: 'الحد الأدنى للمبلغ',
    maxAmount: 'الحد الأعلى للمبلغ',
    singleUse: 'استخدام واحد',
    paymentCount: 'المدفوعات',
    expiresAt: 'تاريخ الانتهاء',
    expire: 'إنهاء الصلاحية',
    copyUrl: 'نسخ الرابط',
    referenceType: 'نوع المرجع',
    referenceId: 'المرجع',
  },
  customers: {
    title: 'العملاء',
    customerNumber: 'رقم العميل',
    name: 'الاسم',
    email: 'البريد الإلكتروني',
    mobile: 'الجوال',
    externalId: 'معرف العميل في المنتج',
    paymentMethods: 'طرق الدفع المحفوظة',
    anonymised: 'مجهول الهوية',
    anonymise: 'إخفاء الهوية (حذف بموجب نظام حماية البيانات)',
    anonymiseWarning: 'يتم استبدال البيانات الشخصية نهائياً. تبقى السجلات المالية كما هي.',
    defaultMethod: 'الافتراضية',
    usable: 'قابلة للاستخدام',
    expired: 'منتهية',
    payments: 'المدفوعات',
    subscriptions: 'الاشتراكات',
  },
  plans: {
    title: 'الخطط',
    code: 'الرمز',
    name: 'الاسم',
    interval: 'دورة الفوترة',
    price: 'السعر',
    monthlyEquivalent: 'المعادل الشهري',
    trialDays: 'أيام التجربة',
    graceDays: 'أيام السماح',
    vatRate: 'نسبة الضريبة',
    customPriced: 'سعر مخصص',
    active: 'نشطة',
    features: 'المزايا',
    create: 'إضافة خطة',
    edit: 'تعديل الخطة',
    sortOrder: 'ترتيب العرض',
  },
  products: {
    title: 'المنتجات',
    code: 'الرمز',
    name: 'الاسم',
    webhookUrl: 'رابط الويب هوك',
    webhookSecret: 'سر الويب هوك',
    generateSecret: 'إنشاء سر',
    rotateSecret: 'تدوير السر',
    redirectUrls: 'روابط التحويل المسموحة',
    addRedirectUrl: 'إضافة رابط تحويل',
    allowSubPaths: 'السماح بالمسارات الفرعية',
    kind: 'النوع',
    tenants: 'المستأجرون',
    defaultCurrency: 'العملة الافتراضية',
    expiryMinutes: 'مدة صلاحية الدفع (دقائق)',
    vatRate: 'نسبة الضريبة',
    prorateUpgrades: 'خصم الترقية فوراً',
    statementDescriptor: 'وصف كشف الحساب',
    supportEmail: 'بريد الدعم',
    create: 'إضافة منتج',
    edit: 'تعديل المنتج',
    active: 'نشط',
    secretShownOnce: 'انسخ السر الآن، فهو مخزن مشفراً ولن يُعرض مرة أخرى.',
  },
  billing: {
    title: 'الفواتير',
    invoiceNumber: 'رقم الفاتورة',
    subtotal: 'المجموع قبل الضريبة',
    discount: 'الخصم',
    vat: 'ضريبة القيمة المضافة',
    total: 'الإجمالي',
    amountPaid: 'المدفوع',
    amountDue: 'المستحق',
    dueDate: 'تاريخ الاستحقاق',
    issuedAt: 'تاريخ الإصدار',
    paidAt: 'تاريخ الدفع',
    period: 'الفترة',
    unpaidOnly: 'غير المدفوعة فقط',
    lineItems: 'بنود الفاتورة',
    quantity: 'الكمية',
    unitPrice: 'سعر الوحدة',
    lineTotal: 'إجمالي البند',
    notAccounting: 'فواتير براح باي سجلات داخلية. يبقى ميزان المصدر المحاسبي الرسمي.',
  },
  events: {
    title: 'الأحداث',
    eventNumber: 'رقم الحدث',
    eventType: 'النوع',
    version: 'الإصدار',
    aggregate: 'الكيان',
    occurredAt: 'وقت الحدوث',
    publishedAt: 'وقت النشر',
    nextAttempt: 'المحاولة القادمة',
    lastError: 'آخر خطأ',
    catalogue: 'فهرس الأحداث',
    payload: 'المحتوى',
    retry: 'إعادة المحاولة',
  },
  audit: {
    title: 'سجل التدقيق',
    action: 'الإجراء',
    entityType: 'نوع الكيان',
    entityId: 'الكيان',
    actor: 'المنفذ',
    actorType: 'نوع المنفذ',
    ipAddress: 'عنوان الـ IP',
    oldValue: 'قبل',
    newValue: 'بعد',
    succeeded: 'نجح',
    occurredAt: 'الوقت',
    appendOnly: 'سجلات التدقيق للإضافة فقط ولا يتم تعديلها أو حذفها.',
  },
  health: {
    title: 'حالة النظام',
    environment: 'البيئة',
    version: 'الإصدار',
    database: 'قاعدة البيانات',
    reachable: 'متصلة',
    unreachable: 'غير متصلة',
    eventTransport: 'ناقل الأحداث',
    cacheProvider: 'مزود التخزين المؤقت',
    queues: 'الطوابير والمتأخرات',
    outboxPending: 'صندوق الصادر المعلق',
    outboxFailed: 'صندوق الصادر الفاشل',
    outboxDeadLetter: 'صندوق الصادر الفاشل نهائياً',
    productWebhooksPending: 'إشعارات المنتجات المعلقة',
    productWebhooksDeadLetter: 'إشعارات المنتجات الفاشلة نهائياً',
    notificationsQueued: 'التنبيهات في الطابور',
    webhooksAwaiting: 'إشعارات واردة بانتظار المعالجة',
    openAlerts: 'تنبيهات مفتوحة',
    adapters: 'محولات المزودين المسجلة',
    serverTime: 'وقت الخادم',
    alerts: 'التنبيهات',
    notifications: 'الإشعارات',
    acknowledge: 'إقرار',
    resolveAlert: 'معالجة',
    severity: 'الخطورة',
    raisedAt: 'وقت الإصدار',
    occurrences: 'عدد التكرار',
  },
  investigations: {
    title: 'التحقيقات',
    reason: 'السبب',
    resolution: 'النتيجة',
    openedBy: 'بدأه',
    openedAt: 'تاريخ البدء',
    resolvedBy: 'أغلقه',
    statusAtOpen: 'الحالة عند البدء',
    providerReported: 'ما أبلغ عنه المزود',
    lateSuccessNotice:
      'أبلغ المزود بنجاح العملية بعد انتهاء صلاحيتها. لا تُفقد الأموال بصمت — أغلق التحقيق لتسجيل النتيجة.',
    resolve: 'إغلاق التحقيق',
    confirmProviderSuccess: 'تأكيد نجاح العملية عند المزود',
    confirmProviderFailure: 'تأكيد عدم نجاح العملية عند المزود',
    refundIssued: 'تم الاسترداد للعميل',
    noActionRequired: 'لا يلزم إجراء',
  },
  checkout: {
    payNow: 'ادفع الآن',
    amountToPay: 'المبلغ المطلوب',
    enterAmount: 'أدخل المبلغ',
    choosePaymentMethod: 'اختر طريقة الدفع',
    securePayment: 'دفع آمن',
    securedBy: 'تتم معالجة المدفوعات بأمان عبر براح باي.',
    poweredBy: 'مدعوم بواسطة براح باي',
    expiresAt: 'تنتهي صلاحية طلب الدفع',
    reference: 'المرجع',
    paymentSuccessful: 'تم الدفع بنجاح',
    paymentFailed: 'لم تكتمل عملية الدفع',
    paymentPending: 'في انتظار الدفع',
    paymentExpired: 'انتهت صلاحية طلب الدفع',
    paymentCancelled: 'تم إلغاء الدفع',
    underReview: 'عملية الدفع قيد المراجعة',
    returnToMerchant: 'العودة إلى {{merchant}}',
    redirecting: 'جارٍ تحويلك إلى بنكك بشكل آمن',
    notAvailable: 'صفحة الدفع غير متوفرة',
    tryAgain: 'إعادة المحاولة',
    contactSupport: 'تواصل مع الدعم',
    minAmount: 'الحد الأدنى {{amount}}',
    maxAmount: 'الحد الأعلى {{amount}}',
    cardDetailsNotice: 'يتم إدخال بيانات البطاقة في صفحة البنك، وليس في براح باي.',
    threeDSecure: 'التحقق الثلاثي',
  },
};

export const resources = {
  en: { translation: en },
  ar: { translation: ar },
};

export type TranslationResources = typeof en;

/** Creates the shared i18next instance. Each app calls this once at start-up. */
export function createI18n(initialLanguage: string = 'en'): i18n {
  const language = SUPPORTED_LANGUAGES.includes(initialLanguage as SupportedLanguage)
    ? initialLanguage
    : 'en';

  if (!i18next.isInitialized) {
    // initReactI18next registers the instance with react-i18next; without it `useTranslation` has no
    // instance to read and every component renders the raw key instead of the translation.
    void i18next.use(initReactI18next).init({
      resources,
      lng: language,
      fallbackLng: 'en',
      interpolation: { escapeValue: false },
      returnNull: false,
    });
  }

  applyDocumentDirection(language);
  return i18next;
}

export { i18next };
